import { useState } from "react"
import { useDispatch } from "react-redux"
import { StoreDispatch } from "../../store/configure-store"
import { FormInputText } from "../../cmps/forms/input/form-input-text"
import { Backlog } from "../../cmps/util-cmps/backlog"
import { Spinner } from "../../cmps/util-cmps/spinner"
import { setUserMsg } from "../../store/user"
import { httpService } from "../../services/http-service"
import { resolveMasterCompanyId } from "../../services/resolve-master-company-id"
import { createEntityMutation } from "../../cmps/util-cmps/graphql-mutations/create-entity"
import { updateEntityContextMutation } from "../../cmps/util-cmps/graphql-mutations/update-entity-context"
import { useEscapeKey } from "../../hooks/use-escape-key"
import { useScrollLock } from "../../hooks/use-scroll-lock"
import { useFocusTrap } from "../../hooks/use-focus-trap"
import { useDraggable } from "../../hooks/use-draggable"

interface EntityFormModalProps {
    companyName: string
    companyId: number
    entityToEdit?: any
    existingEntities: any[]
    onSuccess: () => void
    onClose: () => void
}

const toFormattedJson = (val: any): string => {
    if (!val) return ''
    if (typeof val === 'string') {
        try { return JSON.stringify(JSON.parse(val), null, 4) } catch { return val }
    }
    if (Array.isArray(val) && val.length === 0) return ''
    if (typeof val === 'object' && Object.keys(val).length === 0) return ''
    return JSON.stringify(val, null, 4)
}

const getEmptyForm = () => ({
    name: '',
    abbreviation: '',
    domains: '',
})

const getEditForm = (entity: any) => {
    const ctx = entity.entityContext || {}
    return {
        bucketName: ctx.bucketName || '',
        ITEmail: ctx.ITEmail || '',
        environmentsUrl: ctx.environmentsUrl || '',
        whitelistDepartments: toFormattedJson(ctx.whitelistDepartments),
        whitelistApprovers: toFormattedJson(ctx.whitelistApprovers),
        keyFinancialFoldersList: toFormattedJson(ctx.keyFinancialFoldersList),
        keyFinancialFoldersWhitelistDepartments: toFormattedJson(ctx.keyFinancialFoldersWhitelistDepartments),
        keyFinancialFoldersWhitelistUsers: toFormattedJson(ctx.keyFinancialFoldersWhitelistUsers),
        keyFinancialFoldersWhitelistDepartmentsColumnName: ctx.keyFinancialFoldersWhitelistDepartmentsColumnName || '',
        githubRepos: toFormattedJson(ctx.githubRepos),
        gitlabProjects: toFormattedJson(ctx.gitlabProjects),
        jiraProjects: toFormattedJson(ctx.jiraProjects),
        domains: toFormattedJson(ctx.domains),
        hrAdminGroups: toFormattedJson(ctx.hrAdminGroups),
        administrationActivitiesAdminsWhitelist: toFormattedJson(ctx.administrationActivitiesAdminsWhitelist),
        administrationActivitiesCriticalActivitiesBlacklist: toFormattedJson(ctx.administrationActivitiesCriticalActivitiesBlacklist),
        mongodbCredentials: toFormattedJson(ctx.mongodbCredentials),
        flowDependency: toFormattedJson(ctx.flowDependency),
        jobsBlacklistUsers: toFormattedJson(ctx.jobsBlacklistUsers),
        jobsBlacklistRoles: toFormattedJson(ctx.jobsBlacklistRoles),
        weekendDays: toFormattedJson(ctx.weekendDays),
    }
}

const JSON_FIELDS = [
    'whitelistDepartments', 'whitelistApprovers',
    'keyFinancialFoldersList', 'keyFinancialFoldersWhitelistDepartments',
    'keyFinancialFoldersWhitelistUsers',
    'githubRepos', 'gitlabProjects', 'jiraProjects', 'domains',
    'hrAdminGroups', 'administrationActivitiesAdminsWhitelist',
    'administrationActivitiesCriticalActivitiesBlacklist',
    'mongodbCredentials', 'flowDependency',
    'jobsBlacklistUsers', 'jobsBlacklistRoles', 'weekendDays',
] as const

const FIELD_LABELS: Record<string, string> = {
    whitelistDepartments: 'Whitelisted Departments',
    whitelistApprovers: 'Whitelisted Approvers',
    keyFinancialFoldersList: 'Key Financial Folders List',
    keyFinancialFoldersWhitelistDepartments: 'Key Financial Folders — Whitelisted Departments',
    keyFinancialFoldersWhitelistUsers: 'Key Financial Folders — Whitelisted Users',
    githubRepos: 'GitHub Repos',
    gitlabProjects: 'GitLab Projects',
    jiraProjects: 'Jira Projects',
    domains: 'Domains',
    hrAdminGroups: 'HR Admin Groups',
    administrationActivitiesAdminsWhitelist: 'Admin Activities — Admins Whitelist',
    administrationActivitiesCriticalActivitiesBlacklist: 'Admin Activities — Critical Activities Blacklist',
    mongodbCredentials: 'MongoDB Credentials',
    flowDependency: 'Flow Dependency',
    jobsBlacklistUsers: 'Jobs — Blacklisted Users',
    jobsBlacklistRoles: 'Jobs — Blacklisted Roles',
    weekendDays: 'Weekend Days',
}

export const EntityFormModal: React.FC<EntityFormModalProps> = ({
    companyName, companyId, entityToEdit, existingEntities, onSuccess, onClose
}) => {
    const isEditMode = !!entityToEdit
    const dispatch = useDispatch<StoreDispatch>()
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [form, setForm] = useState<Record<string, string>>(() =>
        isEditMode ? getEditForm(entityToEdit) : getEmptyForm()
    )
    const [jsonErrors, setJsonErrors] = useState<Record<string, string>>({})

    useEscapeKey(onClose)
    useScrollLock()
    const trapRef = useFocusTrap<HTMLDivElement>()
    const { modalRef, onHeaderMouseDown } = useDraggable<HTMLElement>()

    const onChangeText = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target
        setForm(prev => ({ ...prev, [name]: value }))
        if (jsonErrors[name]) {
            setJsonErrors(prev => {
                const next = { ...prev }
                delete next[name]
                return next
            })
        }
    }

    const formatJson = (fieldName: string) => {
        const val = form[fieldName]?.trim()
        if (!val) return
        try {
            const parsed = JSON.parse(val)
            setForm(prev => ({ ...prev, [fieldName]: JSON.stringify(parsed, null, 4) }))
            setJsonErrors(prev => {
                const next = { ...prev }
                delete next[fieldName]
                return next
            })
        } catch {
            setJsonErrors(prev => ({ ...prev, [fieldName]: 'Invalid JSON' }))
        }
    }

    const validateCreate = (): string | null => {
        if (!form.name?.trim()) return 'Name is required'
        if (!form.abbreviation?.trim()) return 'Abbreviation is required'

        const duplicate = existingEntities.some((e: any) =>
            e.name?.toLowerCase() === form.name.trim().toLowerCase()
        )
        if (duplicate) return 'An entity with this name already exists'
        return null
    }

    const validateEdit = (): string | null => {
        for (const field of JSON_FIELDS) {
            const val = form[field]?.trim()
            if (val) {
                try { JSON.parse(val) } catch {
                    return `${FIELD_LABELS[field] || field} must be valid JSON`
                }
            }
        }
        return null
    }

    const onSubmit = async () => {
        const error = isEditMode ? validateEdit() : validateCreate()
        if (error) {
            dispatch(setUserMsg({ msg: { type: 'danger', txt: error } }))
            return
        }

        setIsSubmitting(true)
        try {
            if (isEditMode) {
                const query = updateEntityContextMutation()
                const variables: any = {
                    entityContextId: Number(entityToEdit.entityContext.id),
                    ITEmail: form.ITEmail?.trim() || null,
                    environmentsUrl: form.environmentsUrl?.trim() || null,
                    keyFinancialFoldersWhitelistDepartmentsColumnName:
                        form.keyFinancialFoldersWhitelistDepartmentsColumnName?.trim() || null,
                }

                // All JSON fields — send as JSON string (JSONString scalar expects string input)
                for (const field of JSON_FIELDS) {
                    const val = form[field]?.trim()
                    variables[field] = val ? JSON.stringify(JSON.parse(val)) : null
                }

                console.log('updateEntityContext variables:', JSON.stringify(variables, null, 2))
                const res = await httpService.graphql(companyName, { query, variables })
                console.log('updateEntityContext response:', JSON.stringify(res))
                if (res?.errors?.length) throw new Error(res.errors[0]?.message || 'Update failed')
                if (!res?.updateEntityContext?.entityContext) throw new Error('Update failed')
                dispatch(setUserMsg({ msg: { type: 'success', txt: 'Entity updated successfully' } }))
            } else {
                const domains = form.domains?.trim() || ''
                // CreateEntity writes to master DB — must use master's company id, not silo's local id.
                const masterCompanyId = await resolveMasterCompanyId(companyName, companyId)
                const query = createEntityMutation(
                    masterCompanyId,
                    form.name.trim(),
                    form.abbreviation.trim(),
                    domains
                )
                const res = await httpService.graphql(companyName, query)
                if (res?.errors?.length) throw new Error(res.errors[0]?.message || 'Create failed')
                if (!res?.createEntity?.entity) throw new Error('Create failed')
                dispatch(setUserMsg({ msg: { type: 'success', txt: 'Entity created successfully' } }))
            }
            onSuccess()
        } catch (err: any) {
            const serverMsg = err?.response?.data?.errors?.[0]?.message || err?.message || ''
            const fallback = isEditMode ? 'Failed updating entity' : 'Failed creating entity'
            dispatch(setUserMsg({ msg: { type: 'danger', txt: serverMsg || fallback } }))
        } finally {
            setIsSubmitting(false)
        }
    }

    const renderJsonTextarea = (fieldName: string, rows: number = 4) => {
        const label = FIELD_LABELS[fieldName] || fieldName
        const hasError = !!jsonErrors[fieldName]
        return (
            <div className="textarea-field" key={fieldName}>
                <label htmlFor={fieldName}>{label}</label>
                <textarea
                    name={fieldName}
                    id={fieldName}
                    className={`form-textarea${hasError ? ' textarea-error' : ''}`}
                    value={form[fieldName] || ''}
                    onChange={onChangeText}
                    onBlur={() => formatJson(fieldName)}
                    rows={rows}
                    placeholder="[]"
                />
                {hasError && <span className="field-error">{jsonErrors[fieldName]}</span>}
            </div>
        )
    }

    const modalTitle = isEditMode ? 'Edit Entity' : 'Create Entity'

    if (!isEditMode) {
        return <div ref={trapRef}>
            <section ref={modalRef} className="entity-form-modal" role="dialog" aria-modal="true" aria-label={modalTitle}>
                <header onMouseDown={onHeaderMouseDown}>
                    <h2>{modalTitle}</h2>
                    <button className="close-btn" onClick={onClose} aria-label="Close">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                    </button>
                </header>

                <section className="modal-form-body">
                    <div className="form-section-card">
                        <div className="form-section-label">Entity Details</div>
                        <div className="form-row">
                            <div className="field">
                                <FormInputText keyName="name" label="Name" isRequired={true} value={form.name || ''} onChange={onChangeText} />
                            </div>
                            <div className="field">
                                <FormInputText keyName="abbreviation" label="Abbreviation" isRequired={true} value={form.abbreviation || ''} onChange={onChangeText} />
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="field">
                                <FormInputText keyName="domains" label="Domains" isRequired={false} value={form.domains || ''} onChange={onChangeText} placeholder="comma-separated" />
                            </div>
                        </div>
                    </div>

                    <div className="form-actions">
                        {!isSubmitting && <button className="main" onClick={onSubmit} disabled={isSubmitting}>Create</button>}
                        {isSubmitting && <span className="spinner"><Spinner isSmall={true} /></span>}
                    </div>
                </section>
            </section>
            <Backlog zIndex={199} onCloseBacklog={onClose} />
        </div>
    }

    return <div ref={trapRef}>
        <section ref={modalRef} className="entity-form-modal" role="dialog" aria-modal="true" aria-label={modalTitle}>
            <header onMouseDown={onHeaderMouseDown}>
                <h2>{modalTitle}</h2>
                <button className="close-btn" onClick={onClose} aria-label="Close">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                </button>
            </header>

            <section className="modal-form-body">
                <div className="form-section-card">
                    <div className="form-section-label">Entity</div>
                    <div className="form-row three-col">
                        <div className="field">
                            <label>Name</label>
                            <input type="text" value={entityToEdit.name || ''} disabled />
                        </div>
                        <div className="field">
                            <label>Abbreviation</label>
                            <input type="text" value={entityToEdit.abbreviation || ''} disabled />
                        </div>
                        <div className="field">
                            <label>Onboarding Date</label>
                            <input type="text" value={entityToEdit.onboardingDate || ''} disabled />
                        </div>
                    </div>
                </div>

                <div className="form-section-card">
                    <div className="form-section-label">General</div>
                    <div className="form-row">
                        <div className="field">
                            <FormInputText keyName="ITEmail" label="IT Email" value={form.ITEmail || ''} onChange={onChangeText} />
                        </div>
                    </div>
                    <div className="form-row">
                        <div className="field">
                            <FormInputText keyName="environmentsUrl" label="Environments URL" value={form.environmentsUrl || ''} onChange={onChangeText} />
                        </div>
                        <div className="field">
                            <FormInputText keyName="keyFinancialFoldersWhitelistDepartmentsColumnName" label="Key Financial Folders — Departments Column Name" value={form.keyFinancialFoldersWhitelistDepartmentsColumnName || ''} onChange={onChangeText} />
                        </div>
                    </div>
                </div>

                <div className="form-section-card">
                    <div className="form-section-label">Access Control</div>
                    <div className="json-fields-grid">
                        {renderJsonTextarea('whitelistDepartments', 3)}
                        {renderJsonTextarea('whitelistApprovers', 3)}
                    </div>
                </div>

                <div className="form-section-card">
                    <div className="form-section-label">Key Financial Folders</div>
                    <div className="json-fields-grid">
                        {renderJsonTextarea('keyFinancialFoldersList', 3)}
                        {renderJsonTextarea('keyFinancialFoldersWhitelistDepartments', 3)}
                        {renderJsonTextarea('keyFinancialFoldersWhitelistUsers', 3)}
                    </div>
                </div>

                <div className="form-section-card">
                    <div className="form-section-label">Source Systems</div>
                    <div className="json-fields-grid">
                        {renderJsonTextarea('githubRepos', 3)}
                        {renderJsonTextarea('gitlabProjects', 3)}
                        {renderJsonTextarea('jiraProjects', 3)}
                        {renderJsonTextarea('domains', 3)}
                    </div>
                </div>

                <div className="form-section-card">
                    <div className="form-section-label">HR & Administration</div>
                    <div className="json-fields-grid">
                        {renderJsonTextarea('hrAdminGroups', 3)}
                        {renderJsonTextarea('administrationActivitiesAdminsWhitelist', 3)}
                        {renderJsonTextarea('administrationActivitiesCriticalActivitiesBlacklist', 3)}
                        {renderJsonTextarea('weekendDays', 3)}
                    </div>
                </div>

                <div className="form-section-card">
                    <div className="form-section-label">Advanced</div>
                    <div className="json-fields-grid">
                        {renderJsonTextarea('mongodbCredentials', 4)}
                        {renderJsonTextarea('flowDependency', 4)}
                        {renderJsonTextarea('jobsBlacklistUsers', 3)}
                        {renderJsonTextarea('jobsBlacklistRoles', 3)}
                    </div>
                </div>

                <div className="form-actions">
                    {!isSubmitting && <button className="main" onClick={onSubmit} disabled={isSubmitting}>Save</button>}
                    {isSubmitting && <span className="spinner"><Spinner isSmall={true} /></span>}
                </div>
            </section>
        </section>
        <Backlog zIndex={199} onCloseBacklog={onClose} />
    </div>
}
